import * as React from 'react';
import cx from 'classnames';
import { DatasheetProps, useDatasheetController } from './datasheet.controller';
import { TextField } from './components/text-field';
import { TableField } from './components/table-field';
import { useDrop } from 'react-dnd';
import { getClosestCoordinateByStep } from './datasheet.utils';
import { DatasheetHolder } from './components/datasheet-holder';
import { FieldType } from '@common/dto/field/type';
import { DatasheetElement, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { TextElement } from './components/text-element';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';
import { DatasheetTextComponentGetDto } from '@common/dto/datasheet/text-component/get-dto';
import { FieldPickerField } from '../field-picker/components/fields/fields.types';

import './datasheet.styles.scss';

export const Datasheet: React.FC<DatasheetProps> = (props) => {
  const { 
    datasheet,
    datasheetElementDragType, 
    datasheetElements, 
    onDatasheetElementsDrop, 
    onSelectionEnd, 
    isDatasheetElementSelected, 
    onSelectionReset, 
    onDatasheetElementSelect,
    onDatasheetElementUpdate,
    onDatasheetElementResize,
    onDatasheetElementMove,
    className,
    selectedDatasheetElementsCount,
    gridSettingsMargin,
    pageBreaks,
    onTabOrderChange,
    printableDatasheetHolderId,
  } = props;
  const { datasheetReference } = useDatasheetController(props);
  const [canvasSize, setCanvasSize] = React.useState({ minWidth: 0, minHeight: 0 });
  const padding = 400; // extra space to keep grid visible while dragging/scrolling

  const recomputeCanvasSize = React.useCallback(() => {
    const contentBounds = datasheetElements.reduce(
      (acc, element) => {
        const right = element.coordinates.x + element.width;
        const bottom = element.coordinates.y + element.height;

        return {
          maxX: Math.max(acc.maxX, right),
          maxY: Math.max(acc.maxY, bottom),
        };
      },
      { maxX: 0, maxY: 0 }
    );

    const marginWidth = gridSettingsMargin?.show ? gridSettingsMargin.width : 0;
    const marginHeight = gridSettingsMargin?.show ? gridSettingsMargin.height : 0;

    const pageBreakWidth = pageBreaks?.show ? pageBreaks.width : 0;
    const pageBreakHeight = pageBreaks?.show ? pageBreaks.height : 0;

    const scrollContainer = datasheetReference.current;
    const visibleMaxX = scrollContainer ? scrollContainer.scrollLeft + scrollContainer.clientWidth : 0;
    const visibleMaxY = scrollContainer ? scrollContainer.scrollTop + scrollContainer.clientHeight : 0;

    const next = {
      minWidth: Math.max(contentBounds.maxX, marginWidth, pageBreakWidth, visibleMaxX) + padding,
      minHeight: Math.max(contentBounds.maxY, marginHeight, pageBreakHeight, visibleMaxY) + padding,
    };

    setCanvasSize((prev) => (
      prev.minWidth !== next.minWidth || prev.minHeight !== next.minHeight
        ? next
        : prev
    ));
  }, [datasheetElements, gridSettingsMargin, pageBreaks, datasheetReference, padding]);

  React.useEffect(() => {
    recomputeCanvasSize();
  }, [recomputeCanvasSize]);

  React.useEffect(() => {
    const scrollContainer = datasheetReference.current;
    if (!scrollContainer) return;

    let raf: number | null = null;

    const handleScroll = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        recomputeCanvasSize();
      });
    };

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (raf !== null) {
        cancelAnimationFrame(raf);
      }
    };
  }, [datasheetReference, recomputeCanvasSize]);
  const [{}, dropRef] = useDrop({
    accept: datasheetElementDragType,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();

      if (offset && datasheetReference.current) {
        const dropTargetXy = datasheetReference.current.getBoundingClientRect();

        onDatasheetElementsDrop({
          firstDatasheetElementCoordinates: {
            x: datasheet.gridSettings.snapToGrid 
              ? getClosestCoordinateByStep(offset.x - dropTargetXy.left, datasheet.gridSettings.horizontalGrid)
              : offset.x - dropTargetXy.left,
            y: datasheet.gridSettings.snapToGrid 
              ? getClosestCoordinateByStep(offset.y - dropTargetXy.top, datasheet.gridSettings.verticalGrid)
              : offset.y - dropTargetXy.top,
          },
          draggedField: item as FieldPickerField
        });
      }
    },
  });

  return (
    <div className={cx("DatasheetsEditorDatasheet", className)} ref={dropRef}>
      <DatasheetHolder
        printableDatasheetHolderId={printableDatasheetHolderId}
        selectionDisabled={datasheet.gridSettings.showEditTabOrder}
        className="DatasheetsEditorDatasheet__grid" 
        style={{
          minWidth: canvasSize.minWidth,
          minHeight: canvasSize.minHeight,
          backgroundSize: datasheet.gridSettings.showGrid 
            ? `${datasheet.gridSettings.horizontalGrid}px ${datasheet.gridSettings.verticalGrid}px`
            : undefined,
        }} 
        hasSelectedDatasheetElements={Boolean(selectedDatasheetElementsCount)}
        onSelectionReset={onSelectionReset}
        reference={datasheetReference}
        onSelectionEnd={onSelectionEnd}
      >
        {gridSettingsMargin?.show && (
          <div 
            className="DatasheetsEditorDatasheet__grid__margin"
            style={{
              width: gridSettingsMargin?.width,
              height: gridSettingsMargin?.height,
            }}
          />
        )}
        {pageBreaks.show && (
          <div 
            className="DatasheetsEditorDatasheet__grid__page-breaks" 
            style={{
              backgroundSize: `${pageBreaks.width}px ${pageBreaks.height}px`,
            }}
          />
        )}
        {datasheetElements.map((x) => {
          const resizeHandlerStrategy = selectedDatasheetElementsCount > 1 ? 'immediate' : 'end';
          const multipleElementsSelected = selectedDatasheetElementsCount > 1;

          if (x.type === DatasheetElementType.text) {
            return (
              <TextElement 
                isTabOrderSettingActive={datasheet.gridSettings.showEditTabOrder}
                key={x.id}
                onCoordinatesChange={onDatasheetElementMove}
                multipleElementsSelected={selectedDatasheetElementsCount > 1}
                datasheetGridSettings={datasheet.gridSettings}
                datasheetElement={x as DatasheetElement<DatasheetTextComponentGetDto>} 
                onResize={onDatasheetElementResize}
                onSelect={onDatasheetElementSelect}
                isSelected={isDatasheetElementSelected}
                containerRef={datasheetReference}
                onTextChange={onDatasheetElementUpdate}
                resizeHandlerStrategy={resizeHandlerStrategy}
              />
            );
          }

          const datasheetElementFieldComponent = x as DatasheetElement<DatasheetFieldComponentGetDto>;

          // Backend payloads are not fully consistent about which property carries the field type.
          // Prefer `field.fieldtype.typeid`, then fall back to `rawFieldType` / `type`.
          const resolvedFieldTypeId =
            (datasheetElementFieldComponent.original.field.fieldtype?.typeid as number | undefined) ??
            (datasheetElementFieldComponent.original.field.rawFieldType as number | undefined) ??
            (datasheetElementFieldComponent.original.field.type as number | undefined);

          if ([FieldType.table].includes(resolvedFieldTypeId as FieldType)) {
            return (
              <TableField
                isTabOrderSettingActive={datasheet.gridSettings.showEditTabOrder}
                key={datasheetElementFieldComponent.id}
                onTabOrderChange={onTabOrderChange}
                resizeHandlerStrategy={resizeHandlerStrategy}
                multipleElementsSelected={multipleElementsSelected}
                datasheetGridSettings={datasheet.gridSettings}
                datasheetElement={datasheetElementFieldComponent}
                onResize={onDatasheetElementResize}
                onSelect={onDatasheetElementSelect}
                isSelected={isDatasheetElementSelected}
                containerRef={datasheetReference}
                onCoordinatesChange={onDatasheetElementMove}
                parentFormatId={datasheet.id}
              />
            );
          }

          return (
            <TextField 
              isTabOrderSettingActive={datasheet.gridSettings.showEditTabOrder}
              key={datasheetElementFieldComponent.id}
              onTabOrderChange={onTabOrderChange}
              resizeHandlerStrategy={resizeHandlerStrategy}
              multipleElementsSelected={multipleElementsSelected}
              datasheetGridSettings={datasheet.gridSettings}
              datasheetElement={datasheetElementFieldComponent} 
              onResize={onDatasheetElementResize}
              onSelect={onDatasheetElementSelect}
              isSelected={isDatasheetElementSelected}
              containerRef={datasheetReference}
              onCoordinatesChange={onDatasheetElementMove}
            />
          );
        })}
      </DatasheetHolder>
    </div>
  );
};